import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Calendar, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { complianceAPI, WORKFLOW_STAGE_LABELS } from '../../lib/complianceAPI';
import { formatAUDate } from '../../lib/dateUtils';
import { ProposalsPanel } from '../compliance-auto/ProposalsPanel';

const typeLabels = { BAS: 'BAS', IAS: 'IAS', PAYG: 'PAYG', FBT: 'FBT', TAX_RETURN: 'Tax Return' };
const statusColors = {
  overdue: 'bg-red-100 text-red-700',
  due_soon: 'bg-amber-100 text-amber-700',
  pending: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
};

// Client Compliance Tab (spec Module 2J / B#13): shows this client's
// obligations, overdue count, and a quick "generate deadlines" action.
export function ComplianceTab({ clientId }) {
  const [deadlines, setDeadlines] = useState([]);
  const [workflows, setWorkflows] = useState({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    load();
    complianceAPI.workflows().then((res) => setWorkflows(res.data)).catch(() => {});
    // eslint-disable-next-line
  }, [clientId]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await complianceAPI.listClientDeadlines(clientId);
      setDeadlines(res.data);
    } catch (error) {
      toast.error('Failed to load compliance');
    } finally {
      setLoading(false);
    }
  };

  const handleWorkflowChange = async (deadline, workflow_status) => {
    try {
      await complianceAPI.update(deadline.id, { workflow_status });
      toast.success('Progress updated');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update progress');
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await complianceAPI.generateClientDeadlines(clientId);
      toast.success(res.data.message || 'Deadlines generated');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate deadlines');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return <div className="animate-pulse h-48 bg-muted rounded"></div>;
  }

  const overdue = deadlines.filter((d) => d.status === 'overdue').length;

  return (
    <div className="space-y-4">
    <ProposalsPanel clientId={clientId} />
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center justify-between" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          <span className="flex items-center gap-2">
            <Calendar className="w-5 h-5" /> Compliance Obligations
            {overdue > 0 && <Badge className="bg-red-100 text-red-700">{overdue} overdue</Badge>}
          </span>
          <Button size="sm" variant="outline" onClick={handleGenerate} disabled={generating} data-testid="generate-deadlines">
            <RefreshCw className="w-4 h-4 mr-2" /> {generating ? 'Generating...' : 'Generate Deadlines'}
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {deadlines.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            No obligations yet. Click "Generate Deadlines" to auto-create them from this client's profile.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Period</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead>Timing</TableHead>
                <TableHead>Progress</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {deadlines.map((d) => (
                <TableRow key={d.id} data-testid={`client-deadline-${d.id}`}>
                  <TableCell className="font-medium">{typeLabels[d.type] || d.type}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {d.period_start && d.period_end ? `${formatAUDate(d.period_start)} – ${formatAUDate(d.period_end)}` : '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{formatAUDate(d.due_at)}</TableCell>
                  <TableCell>
                    <Badge className={`${statusColors[d.status] || ''} text-xs capitalize`}>{d.status?.replace('_', ' ')}</Badge>
                  </TableCell>
                  <TableCell>
                    {(workflows[d.type] || []).length > 0 ? (
                      <Select value={d.workflow_status || 'not_started'} onValueChange={(v) => handleWorkflowChange(d, v)}>
                        <SelectTrigger className="h-7 w-[170px] text-xs" data-testid={`workflow-${d.id}`}>
                          <SelectValue>{WORKFLOW_STAGE_LABELS[d.workflow_status || 'not_started']}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {workflows[d.type].map((s) => (
                            <SelectItem key={s} value={s}>{WORKFLOW_STAGE_LABELS[s] || s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-xs text-muted-foreground/70 capitalize">{(d.workflow_status || 'not_started').replace('_', ' ')}</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
    </div>
  );
}
